import { LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ltnRouterContext, nsSettingsServiceContext, nsDirectoryServiceContext } from './contexts.js';

import { loadStripe } from '@stripe/stripe-js/pure';
import type {
  Stripe,
  StripeElements,
  StripePaymentElement,
  StripeCardNumberElement,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeAddressElement,
  Address,
} from '@stripe/stripe-js';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { LtnRouter } from '../ltn-router/ltn-router.js';

import { NSSettingsService, SettingsObject } from '../ns-settings/ns-settings-service.js';

import { NSDirectoryService } from './ns-directory-service.js';

import { findRootAppElement } from '../helpers/dom.js';

import { InvoiceAddress } from '../models/invoice.js';
import { paymentElementAddressDefaults } from '../helpers/billing-details.js';
import { palette } from '../ns-styles/tokens/palette.js';

@customElement('ns-stripe-service')
export class NSStripeService extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: ltnRouterContext })
  private __router?: LtnRouter;

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  private __buttressSettings?: SettingsObject;

  private __stripe?: Stripe | null;

  private __stripeLoadPromise?: Promise<Stripe | null>;

  private __elements?: StripeElements;

  private __paymentElement?: StripePaymentElement;

  private __paymentCardNumber?: StripeCardNumberElement;

  private __paymentCardCvc?: StripeCardCvcElement;

  private __paymentCardExpiryDate?: StripeCardExpiryElement;

  private __paymentAddress?: StripeAddressElement;

  private __customerDetails?: {
    name: string;
    address: Address;
  };

  private __slotHolder?: HTMLElement;

  private __slotId?: string;

  private __countryList: {
    code: string;
    countryName: string;
  }[] = [
    {
      code: 'GB',
      countryName: 'United Kingdom',
    },
    {
      code: 'GB',
      countryName: 'Great Britain',
    },
    {
      code: 'US',
      countryName: 'United States of America',
    },
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('A DB Service Is Required!');

    if (!this.__router) throw new Error('Unable to find LtnRouter service');

    if (!this.__dir) throw new Error('Unable to find NSWorkflowService');

    if (!this.__settings) throw new Error('Unable to find settings service');

    // App env
    const app = this.__settings.settings.app as SettingsObject;
    if (!app) throw new Error('Unable to find app settings');

    // Buttress env
    this.__buttressSettings = this.__settings.settings.buttress as SettingsObject;
    if (!this.__buttressSettings) throw new Error('Unable to find buttress settings');
  }

  private async __ensureStripe(): Promise<Stripe> {
    if (!this.__stripe) {
      if (!this.__stripeLoadPromise) this.__stripeLoadPromise = this.__loadStripe();
      this.__stripe = await this.__stripeLoadPromise;
    }
    if (!this.__stripe) throw new Error('Could not load stripe');

    return this.__stripe;
  }

  private async __loadStripe(): Promise<Stripe | null> {
    if (!this.__settings) throw new Error('Unable to find settings service');

    const app = this.__settings.settings.app as SettingsObject;
    if (!app) throw new Error('Unable to find app settings');

    const stripe = this.__settings.settings.stripe as SettingsObject;
    if (!stripe) throw new Error('Unable to find app stripe settings');

    // Both names until the production environment name is settled; anything else gets test keys.
    const isProduction = app.mode === 'prod' || app.mode === 'production';
    const publishableKey = (isProduction ? stripe.publishableLiveKey : stripe.publishableTestKey) as string;
    if (!publishableKey) throw new Error('Missing required publishable key');

    return loadStripe(publishableKey);
  }

  /**
   * Mounts the card form. With a client secret it's Stripe's Payment Element, whose postcode and country start at
   * `cardAddress` when given (the billing details already entered), so the payer isn't asked for them twice.
   */
  async mountStripeElement(
    element: LitElement,
    clientSecret: string | null = null,
    billingAddress: InvoiceAddress | null = null,
    cardAddress: { postcode?: string | null; country?: string | null } | null = null,
  ) {
    const stripe = await this.__ensureStripe();

    const paymentForm = document.querySelector('form#payment-form');
    let cardDetailsWrapper = null;
    let addressWrapper = null;

    if (!paymentForm) {
      const rootElem = findRootAppElement(element, 'stripe-payment-form', this.__slotId);
      const host = rootElem?.element;
      const hostSlotId = rootElem?.id;
      if (!host || !hostSlotId) {
        throw new Error('Failed to inject stripe payment form slot');
      }

      this.__slotId = hostSlotId;
      this.__slotHolder = document.createElement('div');
      this.__slotHolder.slot = `slot-${this.__slotId}`;
      this.__slotHolder.setAttribute('style', 'height: 100%;');
      host.appendChild(this.__slotHolder);

      const paymentElementContainer = document.createElement('form');
      paymentElementContainer.id = 'payment-form';
      paymentElementContainer.innerHTML = `
        <div id="payment-element"></div>
        <div id="error-message"></div>
      `;

      cardDetailsWrapper = document.createElement('div');
      cardDetailsWrapper.classList.add('card-details');
      cardDetailsWrapper.innerHTML = `
        <div class="mr-3">
          <div class="mr-3">Card Number:</div>
          <div id="card-number-element"></div>
        </div>
        <div class="mr-3">
          <div class="mr-3">Card CVC:</div>
          <div id="card-cvc-element"></div>
        </div>
        <div>
          <div class="mr-3">Expiry Date:</div>
          <div id="card-expiry-date-element"></div>
        </div>
      `;
      addressWrapper = document.createElement('div');
      addressWrapper.classList.add('address-wrapper');
      addressWrapper.innerHTML = `
        <div id="card-address-element"></div>
      `;

      paymentElementContainer.appendChild(cardDetailsWrapper);
      paymentElementContainer.appendChild(addressWrapper);

      this.__slotHolder.appendChild(paymentElementContainer);
    }

    const options = {
      appearance: {
        // White fields on a job card's grey panel, as our own inputs there
        rules: {
          '.Label': {
            color: palette.type,
          },
          '.Input': {
            color: palette.type,
            border: 'none',
            boxShadow: 'none',
          },
          '.TermsText': {
            color: palette['grey-dark'],
          },
        },
        variables: {
          colorPrimary: palette['purple'],
          colorBackground: palette.container,
          colorText: palette.type,
          colorTextPlaceholder: palette['grey-dark'],
          colorDanger: palette.red,
          fontFamily: 'Inter',
          spacingUnit: '2px',
          borderRadius: '20px',
          iconColor: palette.type,
          // Our focus ring (2px purple); it fits inside form#payment-form's padding
          focusBoxShadow: `0 0 0 2px ${palette.purple}`,
          focusOutline: 'none',
        },
      },
    } as any;
    // Set up Stripe.js and Elements to use in checkout form, passing the client secret obtained in step 5
    if (clientSecret) {
      options.clientSecret = clientSecret;
    } else {
      options.mode = 'setup';
      options.currency = 'gbp';
      options.setupFutureUsage = 'off_session';
      options.paymentMethodTypes = ['card'];
    }

    this.__elements = stripe.elements(options);

    if (clientSecret) {
      // Create and mount the Payment Element
      this.__paymentElement = this.__elements.create('payment', paymentElementAddressDefaults(cardAddress));
      this.__paymentElement.mount('#payment-element');
    } else {
      this.__paymentCardNumber = this.__elements.create('cardNumber', {
        disableLink: true,
        style: {
          base: {
            color: palette.type,
            fontSize: '16px',
            '::placeholder': {
              color: palette['grey-dark'],
            },
          },
          invalid: {
            color: palette.red,
          },
        },
      });
      this.__paymentCardCvc = this.__elements.create('cardCvc', {
        style: {
          base: {
            color: palette.type,
            fontSize: '16px',
            '::placeholder': {
              color: palette['grey-dark'],
            },
          },
        },
      });
      this.__paymentCardExpiryDate = this.__elements.create('cardExpiry', {
        style: {
          base: {
            color: palette.type,
            fontSize: '16px',
            '::placeholder': {
              color: palette['grey-dark'],
            },
          },
        },
      });
      const countryCode = billingAddress?.country
        ? this.__countryList.find((l) => l.countryName === billingAddress.country)?.code
        : '';
      this.__paymentAddress = this.__elements.create('address', {
        mode: 'billing',
        defaultValues: {
          name: billingAddress?.name || '',
          address: {
            line1: billingAddress?.address1 || '',
            line2: billingAddress?.address2 || '',
            city: billingAddress?.city || '',
            state: '',
            postal_code: billingAddress?.postcode || '',
            country: countryCode || '',
          },
        },
      });

      this.__paymentCardNumber.mount('#card-number-element');
      this.__paymentCardCvc.mount('#card-cvc-element');
      this.__paymentCardExpiryDate.mount('#card-expiry-date-element');
      this.__paymentAddress.mount('#card-address-element');

      cardDetailsWrapper?.classList.add('show');

      this.__paymentAddress.on('change', (event) => {
        if (event.complete) {
          this.__customerDetails = event.value;
        }
      });
    }
  }

  // `returnLambdaPath` is the lambda Stripe sends the browser back to after a redirect (3DS): the subscription payment's by
  // default, `public-ledger/credits/payment-return` for Buy nodeStream credits
  async proceedPayment(jobId: string, returnLambdaPath = 'payment/confirm-payment'): Promise<boolean> {
    if (!this.__dir) throw new Error('Unable to find directory service');
    if (!this.__dir.me || !this.__dir.me.id) throw new Error('missing required profile id');
    if (!this.__router) throw new Error('Can not find LtnRoute service');
    if (!this.__elements) throw new Error('Can not find mounted stripe elements');
    if (!this.__buttressSettings) throw new Error('Unable to find buttress settings');
    if (!jobId) throw new Error('Missing required jobId');

    const stripe = await this.__ensureStripe();

    try {
      const query = `path=${this.__router.route.path}&jobId=${jobId}&personId=${this.__dir.me.id}`;
      const { error } = await stripe.confirmPayment({
        elements: this.__elements,
        confirmParams: {
          return_url: `${this.__buttressSettings.endpoint}/lambda/v1/${this.__buttressSettings.apiPath}/${returnLambdaPath}?${query}`,
        },
      });

      if (error) throw error;

      return true;
    } catch (err: unknown) {
      return this.__showPaymentError(err);
    }
  }

  /**
   * Finishes a payment the server confirmed with a saved card when Stripe asks for more (3DS): shows Stripe's own step and
   * resolves with the payment intent's status afterwards, or throws with Stripe's message.
   */
  async handleNextAction(clientSecret: string): Promise<string | undefined> {
    const stripe = await this.__ensureStripe();
    const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret });
    if (error) throw new Error(error.message ?? 'The payment could not be completed');
    return paymentIntent?.status;
  }

  async confirmPaymentSetup(subscriptionId: string, jobId: string): Promise<boolean> {
    if (!this.__dir || !this.__dir.me) throw new Error('Can not find directory service');
    if (!this.__router) throw new Error('Can not find LtnRoute service');
    if (!this.__elements) throw new Error('Can not find mounted stripe elements');
    if (!jobId) throw new Error('Missing required jobId');

    const stripe = await this.__ensureStripe();

    try {
      const query = `path=${this.__router.route.path}&subscriptionId=${subscriptionId}&jobId=${jobId}&personId=${this.__dir.me.id}`;
      const { error } = await stripe.confirmSetup({
        elements: this.__elements,
        confirmParams: {
          return_url: `%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/payment/confirm-payment-setup?${query}`,
        },
      });

      if (error) throw error;

      return true;
    } catch (err: unknown) {
      return this.__showPaymentError(err);
    }
  }

  /**
   * Confirms a SetupIntent mounted with its client secret (the automatic payments card, #25), staying on the page unless
   * the bank needs a redirect; Stripe then returns the browser to this page with `setup_intent` in the URL. Answers the
   * SetupIntent's id once it has succeeded, or null when Stripe refused the card (shown in the form) or it isn't done yet.
   */
  async confirmCardSetup(): Promise<string | null> {
    if (!this.__elements) throw new Error('Can not find mounted stripe elements');
    const stripe = await this.__ensureStripe();
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements: this.__elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });
      if (error) throw error;
      return setupIntent?.status === 'succeeded' ? setupIntent.id : null;
    } catch (err: unknown) {
      this.__showPaymentError(err);
      return null;
    }
  }

  /**
   * Shows why Stripe or the network refused a payment in the mounted form's error box, and answers false. A validation
   * error is already shown by Stripe's own fields. Any error may arrive here, not only Stripe's, so its type is read
   * carefully; without a mounted form the error is thrown for the card to show.
   */
  private __showPaymentError(err: unknown): false {
    const type = (err as { type?: unknown } | null)?.type;
    if (typeof type === 'string' && type.toUpperCase() === 'VALIDATION_ERROR') return false;

    const message = (err as { message?: unknown } | null)?.message;
    const errorMessage = typeof message === 'string' && message ? message : 'Something went wrong';
    // The box is inside the form this service mounted, which may sit in a shadow root that document can't see into
    const messageContainer = this.__slotHolder?.querySelector('#error-message');
    if (!messageContainer) throw new Error(errorMessage);
    messageContainer.classList.add('show');
    messageContainer.textContent = errorMessage;
    return false;
  }

  cleanElements() {
    this.__slotHolder?.remove();
    this.__paymentElement?.clear();
    this.__paymentCardNumber?.clear();
    this.__paymentCardCvc?.clear();
    this.__paymentCardExpiryDate?.clear();
    this.__paymentAddress?.clear();
  }

  async addPaymentMethodCard() {
    if (!this.__elements || !this.__paymentCardNumber) throw new Error('Can not find stripe elements');

    const stripe = await this.__ensureStripe();

    return stripe.createPaymentMethod({
      type: 'card',
      card: this.__paymentCardNumber,
      billing_details: {
        name: this.__customerDetails?.name || '',
        address: {
          line1: this.__customerDetails?.address.line1 || '',
          line2: this.__customerDetails?.address.line2 || '',
          city: this.__customerDetails?.address.city || '',
          state: this.__customerDetails?.address.state || '',
          postal_code: this.__customerDetails?.address.postal_code || '',
          country: this.__customerDetails?.address.country || '',
        },
      },
    });
  }
}
